import { useEffect, useRef, useState } from 'react';
import Field from './Field';
import { api } from '../api/client';

export const MIN_QUERY_LENGTH = 2;

/**
 * Whether a search should be sent for what is currently in the field.
 *
 * <p>Extracted and pure because getting it wrong is silent: the field looks alive, the
 * request is simply never made. It was wrong — the guard compared the text against the
 * selected school's name, but the input's own {@code onChange} writes every keystroke
 * into that same name, so the two were always equal and the typeahead never fired.
 *
 * <p>The comparison has to be against a name that only changes when a suggestion is
 * actually chosen, which is what {@code chosenName} is.
 */
export function shouldSearch(text, chosenName) {
  const term = String(text ?? '').trim();
  if (term.length < MIN_QUERY_LENGTH) {
    return false;
  }
  // Just picked from the list: searching for it again would reopen the dropdown on
  // the answer the user already gave.
  return term !== String(chosenName ?? '').trim();
}

/**
 * Typeahead against the School Master. A school that is not yet in the master can
 * still be typed in free-text; the API keeps it as schoolName for Module 3 to reconcile.
 *
 * <p>Whether the school was picked or typed matters more than it looks: a typed name
 * leaves the account with no School Master row, and activation codes are allocated to
 * a school rather than to a person. So the field says which of the two has happened
 * rather than leaving both looking identical.
 */
export default function SchoolPicker({ value, onChange, label = 'School name or code', error }) {
  const [text, setText] = useState(value?.name ?? '');
  const [results, setResults] = useState([]);
  const [chosenName, setChosenName] = useState(value?.id ? (value?.name ?? '') : '');
  const [searched, setSearched] = useState(false);
  const [lookupFailed, setLookupFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const blurTimer = useRef(null);

  useEffect(() => {
    if (value?.name && value.name !== text) setText(value.name);
    // Only re-sync when the selection itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.id]);

  useEffect(() => {
    if (!shouldSearch(text, chosenName)) {
      setResults([]);
      setSearched(false);
      return undefined;
    }
    // Debounced so a fast typist sends one request, not one per keystroke.
    const handle = setTimeout(() => {
      api
        .schoolLookup(text.trim())
        .then((found) => {
          setResults(found);
          setSearched(true);
          setLookupFailed(false);
        })
        .catch(() => {
          // Not swallowed: a field that silently stops searching is exactly how this
          // went unnoticed before.
          setResults([]);
          setSearched(true);
          setLookupFailed(true);
        });
    }, 250);
    return () => clearTimeout(handle);
  }, [text, chosenName]);

  useEffect(() => () => clearTimeout(blurTimer.current), []);

  const picked = Boolean(value?.id);
  const noMatch = searched && !lookupFailed && results.length === 0 && !picked;

  return (
    <Field
      label={label}
      id="school"
      error={error}
      hint="Pick your school from the list so your account is linked to it"
    >
      <input
        id="school"
        name="school"
        className="input"
        autoComplete="off"
        maxLength={200}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? 'school-error' : undefined}
        value={text}
        placeholder="Start typing a school name or code"
        onFocus={() => setOpen(true)}
        onBlur={() => {
          // Delay so a click on a suggestion registers before the list unmounts.
          blurTimer.current = setTimeout(() => setOpen(false), 150);
        }}
        onChange={(event) => {
          setText(event.target.value);
          setOpen(true);
          // Typing abandons any previous selection, which is what lets the next
          // keystroke search again.
          setChosenName('');
          onChange({ id: null, name: event.target.value });
        }}
      />

      {open && results.length > 0 && (
        <ul className="suggestions">
          {results.map((school) => (
            <li key={school.id}>
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  setText(school.name);
                  setChosenName(school.name);
                  setResults([]);
                  setOpen(false);
                  onChange({ id: school.id, name: school.name });
                }}
              >
                {school.name}
                <span className="tiny muted"> · {school.code} · {school.city}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {picked && (
        <p className="hint" style={{ color: 'var(--color-accent)' }}>
          Linked to this school in the Fountainhead directory.
        </p>
      )}

      {noMatch && (
        <p className="hint">
          No match in the directory. Your school will be recorded as typed, and
          Fountainhead will link it for you.
        </p>
      )}

      {lookupFailed && (
        <p className="hint">
          Could not search just now. You can still type your school name and continue.
        </p>
      )}
    </Field>
  );
}
